// facetTriState.js — the categorical facet's three states, driven by the single filter definition (B1213).
//
//   neutral → ✓ include → ✕ exclude → neutral
//
// ✓ = the candidate must belong to the selected set (OR within the facet); ✕ = it must NOT belong to the
// excluded set (NOT OR); neutral = the facet does not participate. Across facets AND, as today.
// ⚠️ Categorical ✕ is NOT the feature `:no` (featureStates.js): `pool:no` is explicit evidence of absence;
// `source:ss.ge` excluded means ss.ge is not an eligible matching source. Two operators, two modules.
//
// WHICH facets have an exclude half, and what their state keys are, is read from core/search-filter-spec.json
// — never listed here. A facet without `exclude` in the spec cycles neutral → ✓ → neutral (two states), so
// a control cannot land on a state the search cannot honour.
import spec from '../../core/search-filter-spec.js'

const EXCLUDE_OF = Object.fromEntries(spec.filters.filter(f => f.exclude && f.client).map(f => [f.key, f.exclude.state]))
// A FLAG facet (topFloor, firstFloor) is one value: its state is the boolean itself, its ✕ half another boolean.
const IS_FLAG = Object.fromEntries(spec.filters.filter(f => f.kind === 'flag').map(f => [f.key, true]))
export function isFlagFacet(includeKey) { return !!IS_FLAG[includeKey] }

/** The state key that holds a facet's excluded values, or null when the facet has no exclude half. */
export function excludeKeyOf(includeKey) { return EXCLUDE_OF[includeKey] || null }

/** Every (include, exclude) key pair the spec declares — what the registry's derived rows are built from. */
export function excludePairs() { return Object.entries(EXCLUDE_OF) }

/** 'include' | 'exclude' | null for one value of a facet. */
export function stateOf(filters, includeKey, value) {
  if (IS_FLAG[includeKey]) { const ex = EXCLUDE_OF[includeKey]; return filters?.[includeKey] === true ? 'include' : (ex && filters?.[ex] === true) ? 'exclude' : null }
  if ((filters?.[includeKey] || []).includes(value)) return 'include'
  const ex = EXCLUDE_OF[includeKey]
  if (ex && (filters?.[ex] || []).includes(value)) return 'exclude'
  return null
}

/** The next state of the cycle for this facet (two states when the spec declares no exclude half). */
export function nextState(cur, includeKey) {
  const canExclude = !!EXCLUDE_OF[includeKey]
  if (cur == null) return 'include'
  if (cur === 'include') return canExclude ? 'exclude' : null
  return null
}

/** The filter patch that moves one value to its next state. A value is never in both halves at once. */
export function cycle(filters, includeKey, value) {
  const next = nextState(stateOf(filters, includeKey, value), includeKey)
  return setState(filters, includeKey, value, next)
}

export function setState(filters, includeKey, value, state) {
  const ex = EXCLUDE_OF[includeKey]
  if (IS_FLAG[includeKey]) { const patch = { [includeKey]: state === 'include' }; if (ex) patch[ex] = state === 'exclude'; return patch }
  const inc = (filters?.[includeKey] || []).filter(v => v !== value)
  const exc = ex ? (filters?.[ex] || []).filter(v => v !== value) : null
  const patch = { [includeKey]: state === 'include' ? [...inc, value] : inc }
  if (ex) patch[ex] = state === 'exclude' ? [...exc, value] : exc
  return patch
}

/** Human-readable summary of a facet's current selection: "(a OR b) AND NOT c". */
export function describe(filters, includeKey) {
  const inc = filters?.[includeKey] || [], ex = EXCLUDE_OF[includeKey], exc = ex ? (filters?.[ex] || []) : []
  const parts = []
  if (inc.length) parts.push(inc.length > 1 ? `(${inc.join(' OR ')})` : inc[0])
  if (exc.length) parts.push(`NOT ${exc.length > 1 ? `(${exc.join(' OR ')})` : exc[0]}`)
  return parts.join(' AND ')
}

// ── THE NAME IS NOT THE BOX (owner, 2026-09-06, on the source facet: "pressing on a box must continue to ✕ and
// pressing on a Facebook title must deselect, so if the user wants neutral there is no need to first filter for ✕").
// The BOX cycles (neutral → ✓ → ✕ → neutral); the NAME is a select/deselect toggle: a decided row (✓ or ✕) goes
// straight to neutral, a neutral row becomes ✓. Two affordances, two elements — F-050's one-element rule still holds
// per element: neither is ever swapped under a click.
export function namePress(cur) { return cur == null ? 'include' : null }
/** The filter patch for a press on the NAME of one value. */
export function pressName(filters, includeKey, value) {
  return setState(filters, includeKey, value, namePress(stateOf(filters, includeKey, value)))
}
